import { useEffect, useState } from 'react';
import { markVisited, readLastVisited, type VisitedSurface } from './last-visited';
import { newSince } from './summary-freshness';

/**
 * Mark the surface visited — call it from the screen the reader actually opened (`/routines`,
 * `/tasks`, `/files`), not from the tile that links to it.
 */
export function useMarkVisited(surface: VisitedSurface): void {
  useEffect(() => {
    markVisited(surface);
  }, [surface]);
}

/**
 * The moment "new" starts from for this surface, **captured once at mount**, or `null` before the
 * first effect has run.
 *
 * Reading the stamp live would make the badge disappear underneath the reader: opening `/tasks`
 * writes a fresh stamp, and coming straight back to the hub would re-read it and drop the badge to
 * zero mid-render — which looks like a glitch rather than like "you've seen these now". Capturing at
 * mount means the badge holds still for as long as the reader is on the hub, and is recomputed the
 * next time they arrive.
 *
 * It starts `null` rather than computing during render for a second reason: the server has no
 * localStorage, so a value computed at render time would differ between the server's HTML and the
 * client's first paint and trip hydration. `null` renders no badge on both, and the badge appears on
 * the first client effect.
 */
export function useNewSince(surface: VisitedSurface): number | null {
  const [since, setSince] = useState<number | null>(null);
  useEffect(() => {
    setSince(newSince(readLastVisited(surface), Date.now()));
  }, [surface]);
  return since;
}

/**
 * The moment "new" starts from for a surface, **anchored per PAGE LOAD rather than per mount**.
 *
 * A screen that both reads the stamp and re-stamps it needs the value from BEFORE this visit, and
 * that value cannot live in React state. Two things overwrite it, and each one alone is enough to make
 * the count permanently zero:
 *
 *   1. **`reactStrictMode` double-invokes effects in development**, so a second read inside one mount
 *      sees the stamp the first pass wrote.
 *   2. **The screen remounts.** `useState` resets with it, so the next read gets the stamp the previous
 *      mount wrote.
 *
 * A module-level anchor has exactly the right lifetime. It is set by the first read after a page load
 * and survives every re-render, double-invoke and remount; a real navigation reloads the module and
 * the next visit re-anchors. It also gives the better behaviour on a client-side route away and back:
 * "since your last visit" keeps meaning the visit, rather than resetting to zero because the reader
 * clicked Home again.
 */
const visitAnchors = new Map<VisitedSurface, number>();

export function useVisitedOnce(surface: VisitedSurface): number | null {
  const [since, setSince] = useState<number | null>(null);
  useEffect(() => {
    let anchor = visitAnchors.get(surface);
    if (anchor === undefined) {
      // Read BEFORE marking — and only ever once per page load.
      anchor = newSince(readLastVisited(surface), Date.now());
      visitAnchors.set(surface, anchor);
    }
    setSince(anchor);
    markVisited(surface);
  }, [surface]);
  return since;
}

/** Drop the anchors. For tests only — a page load is the real reset. */
export function resetVisitAnchors(): void {
  visitAnchors.clear();
}
